<template>
  <v-form lazy-validation ref="form" v-model="valid">
    <v-dialog
      scrollable
      persistent
      :value="value"
      :fullscreen="isMobile()"
      width="1200px"
      @click:outside.stop="closeOperation"
      @keydown.esc="closeOperation"
    >
      <v-card flat tile>
        <v-card-title>
          {{ $t('workSchedules.assignTemp') }}
          <v-spacer/>
          <v-btn icon @click="closeOperation">
            <v-icon>close</v-icon>
          </v-btn>
        </v-card-title>
        <v-divider/>
        <v-card-text class="pa-0">
          <v-container fluid class="pa-0">
            <v-layout>
              <v-row class="pa-0 ma-0">
                <v-col class="pa-0 overflow-y-hidden">
                  <v-row class="px-2 pt-1 pb-0 ma-0" align="center">
                    <v-col class="pa-0" :cols="isMdOrLargerViewport() ? 'auto' : '12'">
                      <v-card-subtitle class="pt-4 pb-2">
                        {{ $t('workSchedules.selectStartEndDates') }}
                      </v-card-subtitle>
                    </v-col>
                    <v-col class="pa-0" :cols="isMdOrLargerViewport() ? 'auto' : '12'">
                      <v-row class="ma-0">
                        <v-col cols="6">
                          <v-menu
                            v-model="startDateMenu"
                            style
                            offset-y
                            :close-on-content-click="false"
                            transition="scale-transition"
                            ref="startDateMenu"
                          >
                            <template v-slot:activator="{ on }">
                              <v-text-field
                                v-on="on"
                                v-model="startDate"
                                required
                                :label="`${$t('notifications.startDate')} ${$i18n.locale === 'en' ?
                                '(YYYY-MM-DD)' : ''}`"
                                :rules="startDateRules"
                                prepend-icon="event"
                                class="px-2"
                                autocomplete="off"
                              />
                            </template>
                            <v-date-picker
                              v-model="startDate"
                              :min="minDate"
                              :max="maxDate"
                              no-title :locale="getDatePickerLocale()"
                            >
                              <v-spacer></v-spacer>
                              <v-btn text color="primary" @click="startDateMenu = false">Cancel</v-btn>
                              <v-btn text color="primary" @click="$refs.startDateMenu.save(startDate)">OK</v-btn>
                            </v-date-picker>
                          </v-menu>
                        </v-col>
                        <v-col cols="6">
                          <v-menu
                            v-model="endDateMenu"
                            offset-y
                            :close-on-content-click="false"
                            transition="scale-transition"
                            autocomplete="off"
                            ref="endDateMenu"
                          >
                            <template v-slot:activator="{ on }">
                              <v-text-field
                                v-on="on"
                                v-model="endDate"
                                required
                                :rules="endDateRules"
                                :label="`${$t('notifications.endDate')} ${$i18n.locale === 'en' ? '(YYYY-MM-DD)' : ''}`"
                                prepend-icon="event"
                                class="px-2"
                                autocomplete="off"
                              />
                            </template>
                            <v-date-picker
                              v-model="endDate"
                              :min="minDate"
                              :max="maxDate"
                              no-title :locale="getDatePickerLocale()"
                            >
                              <v-spacer></v-spacer>
                              <v-btn text color="primary" @click="endDateMenu = false">Cancel</v-btn>
                              <v-btn text color="primary" @click="$refs.endDateMenu.save(endDate)">OK</v-btn>
                            </v-date-picker>
                          </v-menu>
                        </v-col>
                      </v-row>
                      <v-combobox
                        v-model="selectedWorkSchedule"
                        v-if="!isMdOrLargerViewport()"
                        outlined
                        hide-details
                        clearable
                        :items="filteredWorkSchedules"
                        item-text="externalName"
                        prepend-icon="work"
                        label="Work Schedule"
                        class="pb-4 pt-2 px-2"
                      />
                    </v-col>
                    <v-col>
                      <v-switch
                        v-if="isMdOrLargerViewport() && isWorkScheduleRotationFeatureEnabled"
                        class="float-right"
                        v-model="rotationMode"
                        color="secondary"
                        :label="`${$t('actions.createRotation')}`"
                      />
                    </v-col>
                  </v-row>
                  <v-divider></v-divider>
                  <v-row class="px-2 pt-1 pb-0 ma-0" align="center" v-if="isDurationSchedule">
                    <v-col class="pa-0" :cols="isMdOrLargerViewport() ? '4' : '12'">
                      <v-card-subtitle class="pt-4 pb-2">
                        Select start time and breaks for work schedule
                      </v-card-subtitle>
                    </v-col>
                    <v-col class="pa-0" :cols="isMdOrLargerViewport() ? '8' : '12'">
                      <DurationWorkScheduleAssignment v-model="durationWorkSchedule"/>
                    </v-col>
                  </v-row>
                  <v-divider/>
                  <v-row class="pa-0 ma-0" style="height:calc(100% - 75px);">
                    <v-col class="pa-0 ma-0">
                      <v-card tile flat class="fill-height">
                        <v-row class="pa-0 ma-0 fill-height">
                          <v-col
                            v-if="isMdOrLargerViewport()"
                            cols="3"
                            style="border-right: 1px solid #e0e0e0"
                            class="pt-1 pb-0"
                          >
                            <v-row>
                              <v-col>
                                <v-text-field
                                  v-model="searchText"
                                  outlined
                                  clearable
                                  hide-details
                                  :label="`${$t('workSchedules.ws')}`"
                                  :placeholder="`${$t('filters.search')}`"
                                  append-icon="search"
                                  @input="selectedWorkSchedule = null"
                                />
                              </v-col>
                            </v-row>
                            <v-divider/>
                            <v-row>
                              <v-col class="pa-0">
                                <v-list style="height: 323px" class="pa-0 overflow-y-auto">
                                  <v-list-item-group
                                    v-model="selectedWorkSchedule"
                                    v-show="!rotationMode"
                                    color="primary"
                                  >
                                    <v-list-item
                                      v-for="(workSchedule, index) in filteredWorkSchedules"
                                      two-line
                                      :value="filteredWorkSchedules[index]"
                                      v-bind:key="index"
                                    >
                                      <v-list-item-content>
                                        <v-list-item-title>{{ workSchedule.externalName }}</v-list-item-title>
                                        <v-list-item-subtitle>{{ workSchedule.externalCode }}</v-list-item-subtitle>
                                      </v-list-item-content>
                                    </v-list-item>
                                  </v-list-item-group>
                                  <v-list-item-group
                                    v-model="selectedWorkSchedules"
                                    color="primary"
                                    v-show="rotationMode"
                                    multiple
                                  >
                                    <v-list-item
                                      v-for="(workSchedule, index) in filteredWorkSchedules"
                                      v-bind:key="workSchedule.externalCode"
                                      two-line
                                      :value="filteredWorkSchedules[index]"
                                      :disabled="!workSchedule.selected && selectedWorkSchedules.length === 4"
                                      @change="handleWorkScheduleSelection(workSchedule, !workSchedule.selected)"
                                    >
                                      <v-list-item-content>
                                        <v-list-item-title>{{ workSchedule.externalName }}</v-list-item-title>
                                        <v-list-item-subtitle>{{ workSchedule.externalCode }}</v-list-item-subtitle>
                                      </v-list-item-content>
                                    </v-list-item>
                                  </v-list-item-group>
                                </v-list>
                              </v-col>
                            </v-row>
                          </v-col>
                          <v-col
                            :cols="isMdOrLargerViewport() ? 9 : 12"
                            class="overflow-y-auto"
                            style="height:100%"
                          >
                            <v-card v-if="workScheduleSelected && !rotationMode">
                              <v-data-table
                                disable-sort
                                disable-pagination
                                hide-default-footer
                                :headers="tableHeaders"
                                :items="selectedWorkSchedule.days"
                                sort-by="day"
                              >
                                <template v-slot:item.day="{ item }">
                                  {{ item.day }}
                                </template>
                                <template v-slot:item.category="{ item }">
                                  {{ getReadableCategory(item.category) }}
                                </template>
                                <template v-slot:item.startTime="{ item }">
                                  {{ getWorkingStartTime(item) }}
                                </template>
                                <template v-slot:item.endTime="{ item }">
                                  {{ getWorkingEndTime(item) }}
                                </template>
                                <template v-slot:item.breaks="{ item }">
                                  {{ getBreaksSummary(item) }}
                                </template>
                                <template v-slot:item.workingDuration="{ item }">
                                  {{ getWorkingDuration(item) }}
                                </template>
                              </v-data-table>
                            </v-card>
                            <v-card v-if="(rotationMode && selectedWorkSchedules.length === 0) ||
                            !rotationMode && !selectedWorkSchedule" flat class="fill-height text-center">
                              <v-container fluid class="ma-0 pa-0 fill-height">
                                <v-layout>
                                  <v-row align="center" justify="center">
                                    <v-col>
                                      <v-icon large>calendar_today</v-icon>
                                      <v-card-text class="text-h6 pb-0">
                                        {{ $t('workSchedules.nonSelected') }}
                                      </v-card-text>
                                      <v-card-text class="text-caption pt-3">
                                        {{
                                          isMobile() ? $t('workSchedules.selectAbove')
                                            : $t('workSchedules.selectLeft')
                                        }}
                                      </v-card-text>
                                    </v-col>
                                  </v-row>
                                </v-layout>
                              </v-container>
                            </v-card>
                            <v-card v-if="rotationMode && selectedWorkSchedules.length > 0">
                              <v-list>
                                <v-list-item-group color="primary">
                                  <v-list-item
                                    v-for="(workSchedule) in selectedWorkSchedules"
                                    v-bind:key="workSchedule.externalCode"
                                    two-line
                                    @click.native.stop=""
                                  >
                                    <v-list-item-content>
                                      <v-list-item-title>
                                        {{ workSchedule.externalName }}
                                        <v-tooltip bottom v-if="!isMobile()" top>
                                          <template v-slot:activator="{ on }">
                                            <v-icon v-on="on">info</v-icon>
                                          </template>
                                          <template v-for="(day, index) in workSchedule.days"
                                          >
                                            <span v-bind:key="index">
                                            {{
                                                `Day ${day.day}: ${day.segments.length === 0 ? 'Non-Working Day' :
                                                  getWorkingStartTime(day) + '-' +
                                                  getWorkingEndTime(day)}`
                                              }}
                                              <span v-if="day.segments.length !== 0">
                                              {{ ` (${getWorkingDuration(day)})` }}
                                            </span>
                                            <span v-if="day.segments.length !== 0">
                                              {{ `| ${getBreaksSummary(day)}` }}
                                            </span>
                                            <br/>
                                          </span>
                                          </template>
                                        </v-tooltip>
                                      </v-list-item-title>
                                      <v-spacer/>
                                      <v-select
                                        v-model="workSchedulesByExternalCode[workSchedule.externalCode].duration"
                                        outlined
                                        validate-on-blur
                                        :items="dayOptions"
                                        :rules="[rules.required]"
                                        placeholder="None Selected"
                                        label="Days"
                                        hide-details="auto"
                                      />
                                    </v-list-item-content>
                                  </v-list-item>
                                </v-list-item-group>
                              </v-list>
                            </v-card>
                          </v-col>
                        </v-row>
                      </v-card>
                    </v-col>
                  </v-row>
                </v-col>
              </v-row>
            </v-layout>
          </v-container>
        </v-card-text>
        <v-card-actions style="border-top: 1px solid #e0e0e0;">
          <v-spacer/>
          <v-btn
            :disabled="(!rotationMode && (!workScheduleSelected || assignmentRequestSending))
            || (rotationMode && selectedWorkSchedules.length < 2)"
            color="primary"
            @click="validateScheduleAssign"
          >
            <div v-if="assignmentRequestSending" class="pr-2">
              <v-progress-circular :size="20" :width="2" indeterminate/>
            </div>
            <span> {{ assignmentRequestSending ? $t('actions.assigning') : $t('actions.assign') }} </span>
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-form>
</template>

<script>
import { mapActions, mapMutations, mapState } from 'vuex';

import constants from '@/constants';

import hasAppDialog from '@/mixins/hasAppDialog';
import validatable from '@/mixins/validatable';

import DateTimeUtil from '@/util/date-time-util';

import isNil from 'lodash/isNil';
import values from 'lodash/values';
import first from 'lodash/first';
import last from 'lodash/last';
import isEmpty from 'lodash/isEmpty';
import filter from 'lodash/filter';
import sum from 'lodash/sum';
import map from 'lodash/map';
import forEach from 'lodash/forEach';

import hasAppSnackbar from '@/mixins/hasAppSnackbar';
import displayAdaptable from '@/mixins/displayAdaptable';
import hasWorkScheduleFeatures from '@/mixins/hasWorkScheduleFeatures';

import isValid from 'date-fns/isValid';
import isEqual from 'date-fns/isEqual';
import isAfter from 'date-fns/isAfter';
import getDatePickerLocale from '@/mixins/getDatePickerLocale';

import DurationWorkScheduleAssignment from '@/components/approvals/DurationWorkScheduleAssignment.vue';

export default {
  name: 'ApprovalsWorkScheduleAssignmentDialog.vue',

  components: {
    DurationWorkScheduleAssignment,
  },

  props: {
    value: Boolean,
    assignments: Array,
  },

  mixins: [
    hasAppDialog,
    hasAppSnackbar,
    displayAdaptable,
    getDatePickerLocale,
    validatable,
    hasWorkScheduleFeatures,
  ],

  data() {
    return {
      valid: true,
      selectedWorkSchedule: null,
      selectedWorkSchedules: [],
      startDateMenu: false,
      endDateMenu: false,
      startDate: null,
      endDate: null,
      searchText: '',
      assignmentRequestSending: false,
      rotationMode: false,
      startDateRules: [],
      endDateRules: [],
      durationWorkSchedule: this.buildDurationWorkSchedule(),
      tableHeaders: [
        {
          text: this.$t('tableHeaders.day'),
          value: 'day',
          align: 'start',
        },
        {
          text: this.$t('tableHeaders.category'),
          value: 'category',
          align: 'start',
        },
        {
          text: this.$t('tableHeaders.startTime'),
          value: 'startTime',
          align: 'start',
        },
        {
          text: this.$t('tableHeaders.endTime'),
          value: 'endTime',
          align: 'start',
        },
        {
          text: this.$t('tableHeaders.breaks'),
          value: 'breaks',
          align: 'start',
        },
        {
          text: this.$t('tableHeaders.workingDuration'),
          value: 'workingDuration',
          align: 'start',
        },
      ],
    };
  },

  computed: {
    ...mapState(['workSchedulesByExternalCode', 'tempWorkScheduleAssignmentStatusPolling']),

    workScheduleSelected() {
      return !isNil(this.selectedWorkSchedule);
    },

    minDate() {
      const date = new Date();
      return DateTimeUtil.convertDateTimeToDate(new Date(date.setDate(1)));
    },

    maxDate() {
      const date = new Date();
      date.setMonth(date.getMonth() + 12);
      return DateTimeUtil.convertDateTimeToDate(new Date(
        date.getFullYear(),
        date.getMonth(),
        0,
      ));
    },

    filteredWorkSchedules() {
      if (isEmpty(this.assignments)) {
        return [];
      }

      const filteredSchedules = filter(
        values(this.workSchedulesByExternalCode),
        (workSchedule) => {
          if (workSchedule.individualWorkSchedule && (this.assignments.length !== 1
            || workSchedule.individualWorkScheduleUserId !== first(this.assignments).id)
          ) {
            return false;
          }

          const variants = first(this.assignments).assignedTimeRecordingVariants;
          return variants && variants.has(workSchedule.timeRecordingVariant);
        },
      );

      return isNil(this.searchText)
        ? filteredSchedules
        : filter(
          filteredSchedules,
          (workSchedule) => workSchedule.externalName.toLowerCase().includes(this.searchText)
            || workSchedule.externalCode.toLowerCase().includes(this.searchText),
        );
    },
    dayOptions() {
      return Array.from({ length: 35 }, (_, i) => i + 1);
    },

    isDurationSchedule() {
      return this.isDurationWorkSchedule(this.selectedWorkSchedule);
    },
  },

  methods: {
    ...mapActions(['assignTempWorkSchedules', 'assignRotation', 'checkForTempWorkScheduleAssignmentRequestStatuses']),
    ...mapMutations(['updateTempWorkScheduleAssignmentRequestStatuses']),

    closeOperation() {
      this.startDate = null;
      this.endDate = null;
      this.startDateRules = [];
      this.endDateRules = [];
      this.valid = true;
      this.selectedWorkSchedule = null;
      forEach(this.workSchedulesByExternalCode, (workSchedule) => {
        workSchedule.duration = null;
        workSchedule.selected = false;
      });
      this.selectedWorkSchedules = [];
      this.rotationMode = false;
      this.durationWorkSchedule = this.buildDurationWorkSchedule();
      this.$emit('close');
    },

    getReadableCategory(category) {
      switch (category) {
        case constants.WorkSchedule.DayCategory.DAY_MODEL:
          return 'Day Model';
        case constants.WorkSchedule.DayCategory.PLANNED_HOURS:
          return 'Planned Hours';
        case constants.WorkSchedule.DayCategory.WORKING_DAY:
          return 'Working Day';
        case constants.WorkSchedule.DayCategory.OFF:
          return 'Non-Working Day';
        default:
          return 'Unknown';
      }
    },

    getWorkingStartTime(day) {
      if (this.shouldApplyDurationWorkSchedule(day)) {
        return DateTimeUtil.getFormattedHours(this.durationWorkSchedule.startTime);
      }
      return isEmpty(day.segments) ? '-' : DateTimeUtil.getFormattedHours(first(day.segments).startTime);
    },

    getWorkingEndTime(day) {
      if (this.shouldApplyDurationWorkSchedule(day)) {
        return DateTimeUtil.addMinutesToTime(this.durationWorkSchedule.startTime, this.getDurationWSTotal(day));
      }
      return isEmpty(day.segments) ? '-' : DateTimeUtil.getFormattedHours(last(day.segments).endTime);
    },

    getBreaksSummary(day) {
      if (this.shouldApplyDurationWorkSchedule(day)) {
        const wsBreaks = filter(this.durationWorkSchedule.breaks, (b) => b.startTime && b.endTime);
        const pluralize = wsBreaks.length > 1 ? 's' : '';
        return isEmpty(wsBreaks)
          ? 'No breaks defined'
          : `${wsBreaks.length} break${pluralize} (${this.getDurationWSTotalBreak()} minutes)`;
      }
      const breaks = filter(
        day.segments,
        (segment) => segment.category === constants.WorkSchedule.SegmentType.SCHEDULED_UNPAID_BREAK,
      );
      return isEmpty(breaks)
        ? this.$t('clock.noBreaks')
        : `${breaks.length} ${this.$tc('misc.break', breaks.length)}
        (${sum(map(breaks, (unpaidBreak) => unpaidBreak.minuteDuration))} ${this.$t('misc.minutes')})`;
    },

    getWorkingDuration(day) {
      if (this.durationWorkSchedule && day.workingMinutes) {
        const duration = day.workingMinutes;
        return DateTimeUtil.getFormattedDuration(duration);
      }
      const workingSegments = filter(
        day.segments,
        (segment) => segment.category === constants.WorkSchedule.SegmentType.SCHEDULED_WORKING_TIME,
      );

      const minuteDuration = isEmpty(workingSegments)
        ? 0
        : sum(map(workingSegments, (segment) => segment.minuteDuration));

      return DateTimeUtil.getFormattedDuration(minuteDuration);
    },

    getDurationWSTotal(day) {
      if (this.shouldApplyDurationWorkSchedule(day)) {
        return day.workingMinutes + this.getDurationWSTotalBreak();
      }
      if (day) {
        return day.workingMinutes;
      }
      return 0;
    },

    getDurationWSTotalBreak() {
      return sum(
        map(
          filter(this.durationWorkSchedule.breaks, (b) => b.startTime && b.endTime),
          (b) => DateTimeUtil.minutesBetweenTimes(b.startTime, b.endTime),
        ),
      );
    },

    shouldApplyDurationWorkSchedule(day) {
      return this.isDurationSchedule
        && this.durationWorkSchedule
        && this.durationWorkSchedule.startTime
        && day.category !== constants.WorkSchedule.DayCategory.OFF
        && day.workingMinutes
        && day.workingMinutes > 0;
    },

    validateScheduleAssign() {
      this.startDateRules = [
        (input) => !!input || this.$t('snackbar.fieldRequired'),
        (input) => (!!input && isValid(new Date(input))) || this.$t('snackbar.invalidDateFormat'),
      ];

      this.endDateRules = this.startDateRules.concat([
        (input) => {
          const startDate = new Date(this.startDate);
          const endDate = new Date(input);
          return isEqual(endDate, startDate) || isAfter(endDate, startDate)
            || this.$t('snackbar.endDateAfterStart');
        },
      ]);

      setTimeout(() => {
        const valid = this.$refs.form.validate();

        if (valid) {
          if (!this.rotationMode) {
            this.confirmScheduleAssign();
          } else {
            this.confirmRotationAssign();
          }
        }
      });
    },

    handleWorkScheduleSelection(workSchedule, value) {
      this.workSchedulesByExternalCode[workSchedule.externalCode].selected = value;
    },

    confirmScheduleAssign() {
      const externalName = this.selectedWorkSchedule.externalName;

      this.showAppConfirmDialog({
        message: this.$tc('dialogs.confirm.assignTempWs', this.assignments.length,
          { externalName, count: this.assignments.length }),
        confirmText: this.$t('actions.assign'),
        confirmHandler: () => {
          this.assignSchedule();
        },
      });
    },

    confirmRotationAssign() {
      this.showAppConfirmDialog({
        message: this.$t(
          'dialogs.confirm.rotationAssign',
          {
            assignmentLength: this.assignments.length,
            employee: this.$tc('misc.employee', this.assignments.length).toLowerCase(),
          },
        ),
        confirmText: this.$t('actions.assign'),
        confirmHandler: () => this.assignEmpRotation(),
      });
    },

    assignSchedule() {
      const request = {
        userIds: map(this.assignments, (assignment) => assignment.id),
        workScheduleExternalCode: this.selectedWorkSchedule.externalCode,
        startDate: this.startDate,
        endDate: this.endDate,
        durationWorkSchedule: this.durationWorkSchedule,
      };

      this.assignmentRequestSending = true;

      this.assignTempWorkSchedules(request)
      .then(() => {
        this.showSnackbar({
          text: this.$t('snackbar.wsQueued'),
          color: constants.AppColors.SUCCESS,
        });
      })
      .catch((error) => {
        this.showSnackbar({
          text: isNil(error.response.data.message) ? this.$t('snackbar.wsNotQueued') : error.response.data.message,
          color: constants.AppColors.ERROR,
        });
      })
      .finally(() => {
        this.assignmentRequestSending = false;
        this.closeOperation();
      });
    },

    assignEmpRotation() {
      const workScheduleData = [];
      forEach(this.selectedWorkSchedules, (workSchedule) => {
        workScheduleData.push({
          externalCode: workSchedule.externalCode,
          duration: workSchedule.duration,
        });
      });
      const request = {
        userIds: map(this.assignments, (assignment) => assignment.id),
        workSchedules: workScheduleData,
        startDate: this.startDate,
        endDate: this.endDate,
      };

      this.assignmentRequestSending = true;

      this.assignRotation(request)
      .then((response) => {
        this.updateTempWorkScheduleAssignmentRequestStatuses(response.data);
        if (!this.tempWorkScheduleAssignmentStatusPolling) {
          this.checkForTempWorkScheduleAssignmentRequestStatuses();
        }
        this.showSnackbar({
          text: 'Work schedule assignment queued for extraction to EC.',
          color: constants.AppColors.SUCCESS,
        });
      })
      .catch((error) => {
        this.showSnackbar({
          text: isNil(error.response.data.message)
            ? 'Work schedule assignment could not be queued for extraction.'
            : error.response.data.message,
          color: constants.AppColors.ERROR,
        });
      })
      .finally(() => {
        this.assignmentRequestSending = false;
        this.closeOperation();
      });
    },

    buildDurationWorkSchedule() {
      return { startTime: null, breaks: [] };
    },

    isDurationWorkSchedule(workSchedule) {
      return !isNil(workSchedule) && workSchedule.timeRecordingVariant === 'DURATION';
    },
  },

  mounted() {
    forEach(this.workSchedulesByExternalCode, (workSchedule) => {
      Object.assign(this.workSchedulesByExternalCode[workSchedule.externalCode], { selected: false, duration: null });
    });
  },

  watch: {
    selectedWorkSchedule(oldSchedule, newSchedule) {
      if (this.isDurationWorkSchedule(oldSchedule) && !this.isDurationWorkSchedule(newSchedule)) {
        this.durationWorkSchedule = this.buildDurationWorkSchedule();
      }
    },
  },
};
</script>

<style scoped>

</style>
